import classNames from "classnames";
import {
  ButtonBack,
  ButtonNext,
  CarouselContext,
  CarouselProvider,
  Dot,
  Slide,
  Slider,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RTF } from "../elements/RTF";
import { useEntryAnimation } from "../hooks/Visibility";
import { ArrowCircleIcon } from "../icons/ArrowCircleIcon";
import { lookupColourString } from "../lookups";
import { HalfWidthClassName } from "../styles";
import { CollectionContent, CollectionProps } from "./Collection";

const ServicesCarouselInside: React.FC<{
  contents: Array<CollectionContent>;
  onChangedSlide: (index: number) => void;
}> = ({ contents, onChangedSlide }) => {
  const carouselContext = useContext(CarouselContext);
  const slides = useMemo(
    () => contents.map(({ content }) => content),
    [contents],
  );
  useEffect(() => {
    function onChange() {
      onChangedSlide(carouselContext.state.currentSlide);
    }
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext, onChangedSlide]);
  return (
    <>
      <ButtonBack className="z-20 col-span-1 col-start-2 row-start-2 justify-self-start disabled:opacity-0 lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:justify-self-center">
        <ArrowCircleIcon
          className="laptop:h-60px mb-10px h-40px rotate-90 transform-gpu md:mb-0"
          colourClassName={
            contents[carouselContext.state.currentSlide].accentColourClassName
          }
        />
      </ButtonBack>
      <Slider
        className={classNames("row-start-1", "col-span-full col-start-1")}
      >
        {slides.map((s, i) => (
          <Slide key={i} index={i}>
            <div
              className={classNames(
                "w-full transform-gpu",
                i !== 0 && "h-full",
              )}
            >
              {s}
            </div>
          </Slide>
        ))}
      </Slider>
      <ButtonNext className="z-20 col-span-1 col-start-5 row-start-2 justify-self-end disabled:opacity-0 md:col-span-1 md:col-start-9 lg:col-span-2 lg:col-start-15 lg:row-start-1 lg:justify-self-center">
        <ArrowCircleIcon
          className="laptop:h-60px mb-10px h-40px -rotate-90 transform-gpu md:mb-0"
          colourClassName={
            contents[carouselContext.state.currentSlide].accentColourClassName
          }
        />
      </ButtonNext>
    </>
  );
};

export const ServicesCarousel: React.FC<CollectionProps> = ({
  title,
  message,
  contents,
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const onChangedSlide = useCallback(
    (index: number) => setCurrentSlideIndex(index),
    [],
  );
  const currentContent = contents[currentSlideIndex];

  const [sectionRef, animationMode] = useEntryAnimation(0.01);

  const background = currentContent.background ?? "Hyacinth";

  const bgClassName = lookupColourString(background, "bg");
  const textColourClassName = lookupColourString(background, "textForBg");

  return (
    <section
      ref={sectionRef}
      data-component="Services Carousel"
      className={classNames(
        "py-section relative py-48px lg:py-100px",
        bgClassName,
        textColourClassName,
        "transition-colors transition-duration-1000",
      )}
    >
      <div className={classNames("page-grid max-width relative")}>
        {title && (
          <h2
            className={classNames(
              "h3",
              "row-start-1",
              HalfWidthClassName,
              animationMode,
              "animate-appear",
            )}
          >
            {title}
          </h2>
        )}
        {message && (
          <RTF
            content={message}
            className={classNames(
              "md:row-start-2",
              "col-span-full col-start-2 row-start-2 animate-appear pt-20px lg:col-start-3",
              "animate-appear",
              animationMode,
            )}
            fixedParagraphClassName="p3"
          />
        )}
        <CarouselProvider
          className={classNames("col-span-full col-start-1", "page-grid")}
          visibleSlides={1}
          naturalSlideWidth={1}
          naturalSlideHeight={1}
          isIntrinsicHeight={true}
          totalSlides={contents.length}
        >
          <ServicesCarouselInside
            contents={contents}
            onChangedSlide={onChangedSlide}
          />
          <div className="col-span-full col-start-1 row-start-3 space-x-20px justify-self-center">
            {contents.map((_, index) => (
              <Dot
                key={index}
                className={classNames(
                  "h-20px w-20px",
                  currentSlideIndex === index
                    ? lookupColourString(background, "oppositeBg")
                    : "bg-coal",
                )}
                slide={index}
              />
            ))}
          </div>
        </CarouselProvider>
      </div>
    </section>
  );
};
